/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree,
 * logs the error, and displays a fallback UI.
 *
 * Error reporting is extensible via the onError callback prop,
 * which can be used to integrate with Sentry, LogRocket, or other services.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Error report context for tracking services.
 */
export interface ErrorReportContext {
  error: Error;
  errorInfo: ErrorInfo;
  componentStack: string | null;
  timestamp: string;
  url: string;
  userAgent: string;
}

/**
 * Callback type for error reporting integration.
 */
export type ErrorReportCallback = (context: ErrorReportContext) => void;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional callback for external error reporting (Sentry, etc.) */
  onError?: ErrorReportCallback;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId: string | null;
}

/**
 * Generate a short error ID for user reference.
 */
function generateErrorId(): string {
  return crypto.randomUUID().substring(0, 8).toUpperCase();
}

/**
 * Error boundary component to catch and display React errors.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, errorId: generateErrorId() };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorContext: ErrorReportContext = {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack ?? null,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log error to console with structured context
    console.error('ErrorBoundary caught an error:', {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Call external error reporting callback if provided
    if (this.props.onError) {
      try {
        this.props.onError(errorContext);
      } catch (reportingError) {
        console.error('Error reporting callback failed:', reportingError);
      }
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6">
          <AlertCircle className="h-12 w-12 text-danger mb-4" />
          <h2 className="text-lg font-medium">Something went wrong</h2>
          <p className="text-sm text-foreground-muted mt-1 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          {this.state.errorId && (
            <p className="text-xs text-foreground-muted/50 mt-2">
              Error reference: {this.state.errorId}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors text-sm font-medium"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
